import { useEffect } from "react";
import { reaction } from "mobx";

import InputVariableConfiguratorStore from "../input-variable-configurator-store";
import { Variable } from "../types";

/**
 * Hook that sets up a MobX reaction to notify external onChange callback
 * whenever the input variables change in the store.
 */
export const useVariableChangeNotifier = (
  store: InputVariableConfiguratorStore,
  onChange?: (variables: Variable[]) => void,
) => {
  useEffect(() => {
    if (!onChange) {
      return;
    }

    // Create a reaction that watches the inputVariables getter
    // and calls onChange whenever it changes
    const dispose = reaction(
      () => store.inputVariables,
      variables => {
        onChange([...variables]);
      },
      {
        // Fire immediately if variables already exist
        fireImmediately: false,
        // Deep comparison to avoid unnecessary calls
        equals: (a, b) => {
          if (a.length !== b.length) {
            return false;
          }
          return a.every((v, i) => {
            const bVar = b[i];
            return (
              v.id === bVar.id &&
              v.key === bVar.key &&
              JSON.stringify(v.value) === JSON.stringify(bVar.value)
            );
          });
        },
      },
    );

    // Cleanup the reaction on unmount
    return () => dispose();
  }, [store, onChange]);
};
